import { useRef, useEffect } from 'react';
import { asElement } from '../utils/dom.js';
import { LastInteraction } from '../utils/interaction.js';
import { useSharedRef } from './use-shared-ref.js';

/**
 * Detect when focus is lost due to a child being unmounted and give you the opportunity to focus
 * on a different element. Meant to be the basis for other hooks, such as one that is more specific
 * to grids where rows may be removed.
 *
 * @param onDetectedFocusRemoval Called when focus is lost due to a child being unmounted with the element that
 *   had focus. The call props include the element that had focus, and the previous and next sibling
 *   elements of the removed element's entire subtree. That is to say, if a row was removed, and
 *   a button inside that row had focus, `previous` and `next` will be the previous and next
 *   sibling of the row, not the button.
 *
 * @param refIn Optional additional ref to attach to your container. Only necessary if you also need
 *   a ref to the container for other purposes. We may choose to refactor this hook to return a
 *   callback ref in the future, so do not assume the hook returned from this component will always
 *   be a ref object.
 *
 * @returns A ref to attach to the container you want to monitor for focus loss.
 */
function useDetectFocusRemoval(onDetectedFocusRemoval, { ref: refIn, enabled = true } = {}) {
    const ref = useSharedRef(refIn);
    const onDetectedFocusRemovalRef = useRef(onDetectedFocusRemoval);
    onDetectedFocusRemovalRef.current = onDetectedFocusRemoval;
    useEffect(() => {
        const refEl = ref.current;
        if (!enabled || !refEl)
            return;
        const observer = new MutationObserver((mutations) => {
            // A subtree was removed, now we need to check if it held the focused element. Focus has
            // already moved to the body by this point, so we grab the last focused element off our global
            // tracker (which we intentionally don't update when focus returns to the body).
            const focused = LastInteraction.focusedElement;
            // If the focused element is still in the document, it wasn't removed — most likely it (or its
            // ancestor) was reordered, which surfaces as a remove + re-insert. Bail so we don't treat a
            // move as focus loss. Focus was briefly dropped to the body by the move, but React restores
            // it to the still-connected node on its own, so leaving it alone is what keeps focus on the
            // moved item.
            if (focused.isConnected)
                return;
            const predicate = nodeContains(focused);
            for (const mutation of mutations) {
                // mutation.type === 'childList' since that's the only type we subscribed to
                const wasFocusedElementRemoved = nodeListFind(mutation.removedNodes, predicate);
                if (wasFocusedElementRemoved) {
                    const previous = asElement(mutation.previousSibling);
                    const next = asElement(mutation.nextSibling);
                    let root = focused;
                    while (root.parentElement)
                        root = root.parentElement;
                    onDetectedFocusRemovalRef.current({ focused, root, previous, next, ref: refEl });
                    break;
                }
            }
        });
        observer.observe(refEl, { childList: true, subtree: true });
        return () => observer.disconnect();
        // We know `ref` is stable
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);
    return ref;
}
function nodeContains(target) {
    return ((parent) => asElement(parent)?.contains(target) ?? false);
}
function nodeListFind(list, predicate) {
    for (const node of list) {
        if (predicate(node))
            return node;
    }
    return null;
}

export { useDetectFocusRemoval };
//# sourceMappingURL=use-detect-focus-removal.js.map
